import { get, writable } from 'svelte/store';
import { isNonProductionAppBuild } from 'src/helpers/dev_tools';

export type PriorityLayoutDevHudKind = 'emeter' | 'sensor' | 'relay' | 'light';

export type PriorityLayoutDevHudPosition = {
	x: number;
	y: number;
};

const POSITION_KEY = 'shelly_dev_priority_layout_hud_pos';
const LEGACY_POSITION_KEY = 'shelly_dev_emeter_priority_hud_pos';

const readHudPosition = (): PriorityLayoutDevHudPosition | null => {
	if (!isNonProductionAppBuild()) return null;
	try {
		const stored = localStorage.getItem(POSITION_KEY) || localStorage.getItem(LEGACY_POSITION_KEY);
		if (!stored) return null;
		const parsed = JSON.parse(stored) as Partial<PriorityLayoutDevHudPosition>;
		if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return null;
		if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return null;
		return { x: parsed.x, y: parsed.y };
	} catch {
		return null;
	}
};

export const priorityLayoutDevHudPosition = writable<PriorityLayoutDevHudPosition | null>(
	readHudPosition(),
);

priorityLayoutDevHudPosition.subscribe((value) => {
	if (!isNonProductionAppBuild()) return;
	try {
		if (value) localStorage.setItem(POSITION_KEY, JSON.stringify(value));
		else localStorage.removeItem(POSITION_KEY);
	} catch {
		// localStorage may be unavailable
	}
});

export const setPriorityLayoutDevHudPosition = (position: PriorityLayoutDevHudPosition) => {
	priorityLayoutDevHudPosition.set(isNonProductionAppBuild() ? position : null);
};

export const resetPriorityLayoutDevHudPosition = () => {
	priorityLayoutDevHudPosition.set(null);
};

export const priorityLayoutDevHudKind = writable<PriorityLayoutDevHudKind | null>(null);

let priorityLayoutDevHudOwner: symbol | undefined;

/** Keep the HUD mounted outside the sidepanel; last claim wins if two views overlap. */
export const claimPriorityLayoutDevHud = (kind: PriorityLayoutDevHudKind): (() => void) => {
	const token = Symbol('priority-layout-dev-hud');
	priorityLayoutDevHudOwner = token;
	priorityLayoutDevHudKind.set(isNonProductionAppBuild() ? kind : null);
	return () => {
		if (priorityLayoutDevHudOwner !== token) return;
		priorityLayoutDevHudOwner = undefined;
		priorityLayoutDevHudKind.set(null);
	};
};

export const getPriorityLayoutDevHudKind = (): PriorityLayoutDevHudKind | null =>
	get(priorityLayoutDevHudKind);
